import { Chip, Stack } from '@mui/material';

import { CaptionText } from '../../shared/typography/PrimaryTypography';
import { EsgRecordStatus } from '../../../types/syntiro_honey';
import { ReviewFilters } from './useRecordList';

/** Human names for the filters whose value is an opaque id. */
export interface ActiveFilterLabels {
  /** Display name of the filtered metric, when known. */
  metricName?: string;
  /** Display label of the filtered reporting period, when known. */
  periodLabel?: string;
  /** Filename of the filtered source file, when known. */
  fileName?: string;
  /** Display name of the filtered entity, when known. */
  entityName?: string;
}

/** One removable active filter. */
export interface ActiveFilterDescriptor {
  /** Stable React key / test handle. */
  key: string;
  /** Reviewer-facing description, e.g. `File: May data.xlsx`. */
  label: string;
  /**
   * Clears just this filter from whatever set it is given. A FUNCTION rather than a
   * precomputed next-set so the removals compose: "Clear all" folds them over one
   * another, where spreading precomputed sets would restore each other's values and
   * only the last removal would survive.
   */
  clear: (filters: ReviewFilters) => ReviewFilters;
}

/** Reviewer-facing names for the fine-grained statuses. */
const STATUS_LABELS: Record<EsgRecordStatus, string> = {
  PENDING_REVIEW: 'Pending review',
  NEEDS_DISAMBIGUATION: 'Disambiguate',
  FLAGGED: 'Flagged',
  APPROVED: 'Approved',
  REJECTED: 'Rejected'
};

/**
 * Describes the filters that are active but NOT already visible in a dedicated
 * control, so the reviewer can see and undo them.
 *
 * Deliberately excludes the scope (`excludeApproved`, and `status: APPROVED`) and
 * the mapping-trust filters (`mappingStatus`, `mappedFromAlias`): those have their
 * own always-visible controls that show their own state, and repeating them here
 * would re-create the duplicate-control problem this row exists to solve. What is
 * left is exactly the set a reviewer would otherwise have no way of knowing was on.
 * @param {ReviewFilters} filters The committed filters.
 * @param {ActiveFilterLabels} labels Human names for the id-valued filters.
 * @returns {Array<ActiveFilterDescriptor>} One descriptor per hidden active filter.
 */
export function describeActiveFilters(
  filters: ReviewFilters,
  labels: ActiveFilterLabels = {}
): Array<ActiveFilterDescriptor> {
  const active: Array<ActiveFilterDescriptor> = [];
  if (filters.search !== undefined) {
    active.push({
      key: 'search',
      label: `Search: ${filters.search}`,
      clear: (current) => ({ ...current, search: undefined })
    });
  }
  if (filters.metricId !== undefined) {
    active.push({
      key: 'metricId',
      label: `Metric: ${labels.metricName ?? filters.metricId}`,
      clear: (current) => ({ ...current, metricId: undefined })
    });
  }
  if (filters.entityId !== undefined) {
    active.push({
      key: 'entityId',
      label: `Entity: ${labels.entityName ?? filters.entityId}`,
      // The level travels with the id, so clearing one must clear both — a level
      // left behind would be sent with no id and rejected by the API.
      clear: (current) => ({ ...current, entityId: undefined, entityLevel: undefined })
    });
  }
  if (filters.periodId !== undefined) {
    active.push({
      key: 'periodId',
      label: `Period: ${labels.periodLabel ?? filters.periodId}`,
      clear: (current) => ({ ...current, periodId: undefined })
    });
  }
  if (filters.fileId !== undefined) {
    active.push({
      key: 'fileId',
      label: `File: ${labels.fileName ?? 'selected upload'}`,
      clear: (current) => ({ ...current, fileId: undefined })
    });
  }
  if (filters.flags !== undefined) {
    active.push({
      key: 'flags',
      // Set only by the duplicate banner's "View affected records" — one honest
      // label beats echoing raw flag codes at the reviewer.
      label: 'Only duplicate/conflict records',
      clear: (current) => ({ ...current, flags: undefined })
    });
  }
  // An APPROVED status is the scope control's business, not a hidden filter.
  if (filters.status !== undefined && filters.status !== 'APPROVED') {
    active.push({
      key: 'status',
      label: `Status: ${STATUS_LABELS[filters.status]}`,
      clear: (current) => ({ ...current, status: undefined })
    });
  }
  if (filters.band !== undefined) {
    active.push({
      key: 'band',
      label: `Confidence: ${filters.band}`,
      clear: (current) => ({ ...current, band: undefined })
    });
  }
  if (filters.needsFollowUp === true) {
    active.push({
      key: 'needsFollowUp',
      label: 'Needs follow-up',
      clear: (current) => ({ ...current, needsFollowUp: undefined })
    });
  }
  return active;
}

interface ActiveFilterChipsProps {
  /** The committed filters. */
  filters: ReviewFilters;
  /** Human names for the id-valued filters, where the parent knows them. */
  labels?: ActiveFilterLabels;
  /** Called with the full next filter set when a chip is removed or all are cleared. */
  onFiltersChange: (filters: ReviewFilters) => void;
}

/**
 * The single answer to "what am I filtered to?" — one row of removable chips for
 * every active filter that lives behind the Filters panel.
 *
 * This is what makes putting filters in a popover safe: a hidden filter with no
 * visible trace reads as "my records disappeared", which is exactly the failure the
 * old always-visible bar was avoiding at the cost of eight permanent controls.
 * @param {ActiveFilterChipsProps} props The filters and the change handler.
 * @returns {JSX.Element | null} The row, or null when no hidden filter is active.
 */
export default function ActiveFilterChips({
  filters,
  labels = {},
  onFiltersChange
}: ActiveFilterChipsProps) {
  const active = describeActiveFilters(filters, labels);
  if (active.length === 0) {
    return null;
  }
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
      <CaptionText>Filtered by</CaptionText>
      {active.map((descriptor) => (
        <Chip
          key={descriptor.key}
          size="small"
          variant="outlined"
          label={descriptor.label}
          onDelete={() => onFiltersChange(descriptor.clear(filters))}
        />
      ))}
      {active.length > 1 && (
        <Chip
          size="small"
          label="Clear all"
          onClick={() =>
            onFiltersChange(
              active.reduce<ReviewFilters>((carry, descriptor) => descriptor.clear(carry), filters)
            )
          }
        />
      )}
    </Stack>
  );
}
